import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCalendar,
  FiCloud,
  FiCopy,
  FiEdit,
  FiExternalLink,
  FiPlus,
  FiSearch,
  FiServer,
  FiTrash,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';

import {
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  Input,
  PageHeader,
  StatusPill,
  Toolbar,
  cn,
  toast,
} from '../components/ui';
import Modal from '../components/Modal';
import { deleteEntreprise, getAllEntreprises } from '../servicesV2/entrepriseService';
import { getWhatsAppUsage, whatsAppBudget, whatsAppCost } from '../servicesV2/whatsappService';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function computeContractState(endDate) {
  if (!endDate) return null;
  const end = moment(endDate);
  const now = moment();
  if (end.isBefore(now)) return 'expired';
  if (end.diff(now, 'months', true) <= 3) return 'expiring';
  return 'active';
}

function copy(text) {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(
    () => toast.success('Copied', { description: text }),
    () => toast.error('Copy failed'),
  );
}

function HostingBadge({ onPremise, size = 'sm' }) {
  return onPremise ? (
    <Badge variant="neutral" size={size}>
      <FiServer size={11} />
      On-premise
    </Badge>
  ) : (
    <Badge variant="brand" size={size}>
      <FiCloud size={11} />
      Cloud
    </Badge>
  );
}

/* -------------------------------------------------------------------------- */
/*  Delete flow — type-to-confirm, optional DB drop                           */
/* -------------------------------------------------------------------------- */

function DeleteClientModal({ client, close, onDeleted }) {
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState('');
  const [deleteDatabase, setDeleteDatabase] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!client) return null;
  const keyToken = client.Key || client.Nom || '';
  const canConfirm = confirmation.trim() === keyToken && !loading;

  const submit = async () => {
    setLoading(true);
    try {
      await deleteEntreprise({
        entrepriseId: client.id,
        themeId: client.ThemeId,
        entrepriseKey: client.Key,
        deleteDataBase: deleteDatabase,
      });
      toast.success('Client supprimé', { description: client.Nom });
      onDeleted?.();
      close();
    } catch (err) {
      console.error(err);
      toast.error('Échec de la suppression', { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-start gap-3 rounded-md border border-danger-100 bg-danger-50 px-3 py-3">
        <FiAlertTriangle size={18} className="mt-0.5 shrink-0 text-danger-600" />
        <div>
          <p className="text-body-sm font-semibold text-danger-700">
            {t('CLIENTS.DELETE_MODAL.TITLE', { defaultValue: 'Supprimer ce client ?' })}
          </p>
          <p className="mt-0.5 text-caption text-danger-700/90">
            Cette action est irréversible. Les utilisateurs du client perdront l'accès
            {deleteDatabase ? ' et la base de données sera supprimée.' : '.'}
          </p>
        </div>
      </div>

      <Field
        className="mt-4"
        label={
          <>
            Tapez <span className="font-mono text-fg">{keyToken}</span> pour confirmer
          </>
        }
      >
        <Input
          autoFocus
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          placeholder={keyToken}
          invalid={confirmation.length > 0 && confirmation.trim() !== keyToken}
        />
      </Field>

      <label className="mt-4 flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={deleteDatabase}
          onChange={(e) => setDeleteDatabase(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-line text-danger-600 focus:ring-danger-600"
        />
        <span className="text-body-sm text-fg">
          Supprimer également la base de données
          <span className="block text-caption text-fg-muted">
            À cocher uniquement si vous êtes sûr — aucun retour en arrière possible.
          </span>
        </span>
      </label>

      <div className="mt-6 flex items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" onClick={close} disabled={loading}>
          {t('GLOBAL.CANCEL', { defaultValue: 'Annuler' })}
        </Button>
        <Button variant="danger" onClick={submit} disabled={!canConfirm} loading={loading}>
          {t('GLOBAL.DELETE', { defaultValue: 'Supprimer' })}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Drawer peek                                                               */
/* -------------------------------------------------------------------------- */

function ClientDrawer({ client, isOpen, close, onRequestDelete }) {
  if (!client) return null;
  const state = computeContractState(client.endDate);
  const contractDays = client.endDate
    ? moment(client.endDate).diff(moment(), 'days')
    : null;

  return (
    <Drawer
      isOpen={isOpen}
      close={close}
      title={client.Nom}
      subtitle={client.RaisonSocial}
      headerActions={
        <>
          <Button
            as={Link}
            to={`/edit-client/${client.id}`}
            variant="secondary"
            size="sm"
            iconLeft={<FiExternalLink size={14} />}
            onClick={close}
          >
            Ouvrir
          </Button>
        </>
      }
    >
      <Drawer.Body className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
            <FiUsers size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-h2 text-fg truncate">{client.Nom}</p>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => copy(client.Key)}
                className="inline-flex items-center gap-1 font-mono text-caption text-fg-muted transition-colors hover:text-fg"
                title="Copy key"
              >
                {client.Key}
                <FiCopy size={11} />
              </button>
              {state && <StatusPill status={state} />}
              <HostingBadge onPremise={!!client.isOnPremise} />
            </div>
          </div>
        </div>

        <section>
          <h3 className="mb-2 text-micro text-fg-subtle">Détails</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-line bg-surface-muted p-4">
            <DetailRow label="Activité" value={client.Activite} />
            <DetailRow label="Domaine d'application" value={client.DomainApplication} />
            <DetailRow label="Représentant" value={client.representantName} icon={<FiUser size={12} />} />
            <DetailRow label="Raison sociale" value={client.RaisonSocial} />
            <DetailRow
              label="Hébergement"
              value={client.isOnPremise ? 'On-premise' : 'Cloud'}
              icon={client.isOnPremise ? <FiServer size={12} /> : <FiCloud size={12} />}
            />
            {client.isOnPremise && (
              <DetailRow label="API publique" value={client.publicApiUrl} />
            )}
          </dl>
        </section>

        <section>
          <h3 className="mb-2 text-micro text-fg-subtle">Contrat</h3>
          <div className="rounded-lg border border-line bg-surface-muted p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-caption text-fg-subtle">Début</p>
                <p className="mt-0.5 text-body-sm text-fg">
                  {client.startDate
                    ? moment(client.startDate).format('DD MMM YYYY')
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-caption text-fg-subtle">Fin</p>
                <p className="mt-0.5 text-body-sm text-fg">
                  {client.endDate ? moment(client.endDate).format('DD MMM YYYY') : '—'}
                </p>
              </div>
            </div>
            {contractDays != null && (
              <p
                className={cn(
                  'mt-3 inline-flex items-center gap-1.5 text-caption',
                  state === 'expired' && 'text-danger-600',
                  state === 'expiring' && 'text-warning-700',
                  state === 'active' && 'text-fg-muted',
                )}
              >
                <FiCalendar size={12} />
                {contractDays >= 0
                  ? `${contractDays} jour${contractDays > 1 ? 's' : ''} restants`
                  : `Expiré depuis ${-contractDays} jour${-contractDays > 1 ? 's' : ''}`}
              </p>
            )}
          </div>
        </section>
      </Drawer.Body>

      <Drawer.Footer>
        <Button variant="danger-ghost" iconLeft={<FiTrash size={14} />} onClick={onRequestDelete}>
          Supprimer
        </Button>
        <Button
          as={Link}
          to={`/edit-client/${client.id}`}
          variant="primary"
          iconLeft={<FiEdit size={14} />}
          onClick={close}
        >
          Modifier
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}

function DetailRow({ label, value, icon }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 inline-flex items-center gap-1.5 text-body-sm text-fg">
        {icon && <span className="text-fg-subtle">{icon}</span>}
        <span className="truncate">{value || '—'}</span>
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Expiry cell                                                               */
/* -------------------------------------------------------------------------- */

function ExpiryCell({ date }) {
  if (!date) return <span className="text-fg-subtle">—</span>;
  const state = computeContractState(date);
  const d = moment(date);
  return (
    <div className="flex items-center gap-2">
      <span className="text-fg">{d.format('DD MMM YYYY')}</span>
      {state === 'expired' && <StatusPill status="expired" size="sm" />}
      {state === 'expiring' && <StatusPill status="expiring" size="sm" />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function Clients() {
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [whatsApp, setWhatsApp] = useState(null);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await getAllEntreprises();
      setClients(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      toast.error('Chargement impossible', { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // The counter is secondary: the client list must load even if it fails.
    getWhatsAppUsage().then(setWhatsApp, () => setWhatsApp(null));
  }, []);

  const whatsAppByClient = useMemo(
    () => new Map((whatsApp?.tenants || []).map((tenant) => [tenant.entrepriseId, tenant])),
    [whatsApp],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.Nom, c.Key, c.RaisonSocial, c.Activite, c.representantName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [clients, query]);

  const openDrawer = (client) => {
    setSelected(client);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const columns = [
    {
      id: 'name',
      header: t('CLIENTS.TABLE.NOM', { defaultValue: 'Nom' }),
      accessor: 'Nom',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ink-100 text-fg-muted">
            <FiUsers size={14} />
          </span>
          <div className="min-w-0">
            <div className="truncate text-body-sm font-medium text-fg">{row.Nom}</div>
            <div className="truncate text-caption text-fg-subtle">{row.RaisonSocial}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'key',
      header: t('CLIENTS.TABLE.KEY', { defaultValue: 'Key' }),
      accessor: 'Key',
      sortable: true,
      mono: true,
    },
    {
      id: 'activity',
      header: t('CLIENTS.TABLE.ACTIVITY', { defaultValue: 'Activité' }),
      accessor: 'Activite',
      sortable: true,
      cell: (row) => (
        <span className="truncate text-fg-muted">{row.Activite || '—'}</span>
      ),
    },
    {
      id: 'domain',
      header: t('CLIENTS.TABLE.DOMAIN_APPLICATION', { defaultValue: 'Domaine' }),
      accessor: 'DomainApplication',
      cell: (row) => (
        <span className="truncate text-fg-muted">{row.DomainApplication || '—'}</span>
      ),
    },
    {
      id: 'hosting',
      header: t('CLIENTS.TABLE.HOSTING', { defaultValue: 'Hébergement' }),
      accessor: 'isOnPremise',
      sortable: true,
      cell: (row) => <HostingBadge onPremise={!!row.isOnPremise} />,
    },
    {
      id: 'whatsapp',
      header: t('CLIENTS.TABLE.WHATSAPP', { defaultValue: 'WhatsApp (mois)' }),
      align: 'right',
      cell: (row) => {
        const usage = whatsAppByClient.get(row.id);
        if (!usage) return <span className="text-fg-subtle">—</span>;
        if (usage.current == null) return <span className="text-fg-subtle">indisponible</span>;
        const budget = whatsAppBudget(usage, whatsApp);
        return (
          <div className="tabular-nums">
            <p className="text-fg">{usage.current}</p>
            <p className={cn('text-caption', budget.reached ? 'text-warning-700' : 'text-fg-subtle')}>
              {budget.spent} $ / {budget.budget} $
            </p>
          </div>
        );
      },
    },
    {
      id: 'rep',
      header: t('CLIENTS.TABLE.REPRESENTANT', { defaultValue: 'Représentant' }),
      accessor: 'representantName',
      cell: (row) => (
        <span className="inline-flex items-center gap-1.5 text-fg-muted">
          <FiUser size={12} className="text-fg-subtle" />
          {row.representantName || '—'}
        </span>
      ),
    },
    {
      id: 'start',
      header: t('CLIENTS.TABLE.START_DATE', { defaultValue: 'Début' }),
      accessor: 'startDate',
      sortable: true,
      cell: (row) =>
        row.startDate ? (
          <span className="text-fg-muted">{moment(row.startDate).format('DD MMM YYYY')}</span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
    {
      id: 'end',
      header: t('CLIENTS.TABLE.END_DATE', { defaultValue: 'Fin' }),
      accessor: 'endDate',
      sortable: true,
      cell: (row) => <ExpiryCell date={row.endDate} />,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: 88,
      stopRowClick: true,
      cell: (row) => (
        <div className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <IconButton
            as={Link}
            to={`/edit-client/${row.id}`}
            variant="ghost"
            size="sm"
            tooltip="Modifier"
          >
            <FiEdit />
          </IconButton>
          <IconButton
            variant="danger"
            size="sm"
            tooltip="Supprimer"
            onClick={() => setDeleteTarget(row)}
          >
            <FiTrash />
          </IconButton>
        </div>
      ),
    },
  ];

  const expiringCount = clients.filter((c) => computeContractState(c.endDate) === 'expiring').length;

  return (
    <>
      <PageHeader
        title={t('CLIENTS.TITLE', { defaultValue: 'Clients' })}
        count={clients.length}
        description="Tous les tenants actifs sur la plateforme."
        meta={
          (expiringCount > 0 || whatsApp) && (
            <>
              {expiringCount > 0 && (
                <Badge variant="warning" dot>
                  {expiringCount} expire{expiringCount > 1 ? 'nt' : ''} bientôt
                </Badge>
              )}
              {whatsApp && (
                <Badge variant="neutral">
                  WhatsApp {whatsApp.currentMonth} : {whatsApp.total.current} message
                  {whatsApp.total.current > 1 ? 's' : ''} envoyé{whatsApp.total.current > 1 ? 's' : ''} — ~
                  {whatsAppCost(whatsApp.total.current, whatsApp)} $
                </Badge>
              )}
            </>
          )
        }
        actions={
          <Button
            as={Link}
            to="/new-client"
            iconLeft={<FiPlus size={14} />}
            onClick={(e) => {
              // Keep parent click behaviour; Link handles navigation.
              e.stopPropagation?.();
            }}
          >
            {t('CLIENTS.ADD_BTN', { defaultValue: 'Nouveau client' })}
          </Button>
        }
      />

      <Toolbar
        left={
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher par nom, key, activité…"
            leading={<FiSearch size={13} />}
            containerClassName="max-w-sm w-full"
          />
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        rowKey="id"
        onRowClick={openDrawer}
        loading={loading}
        isFiltered={query.length > 0}
        initialSort={{ columnId: 'name', direction: 'asc' }}
        pageSize={25}
        empty={
          <EmptyState
            icon={<FiUsers size={22} />}
            title="Aucun client"
            description="Créez votre premier client pour commencer."
            action={
              <Button as={Link} to="/new-client" iconLeft={<FiPlus size={14} />}>
                Nouveau client
              </Button>
            }
          />
        }
        emptyFiltered={
          <EmptyState
            icon={<FiSearch size={22} />}
            title="Aucun résultat"
            description={`Rien ne correspond à « ${query} ».`}
            action={
              <Button variant="secondary" onClick={() => setQuery('')}>
                Effacer la recherche
              </Button>
            }
          />
        }
      />

      <ClientDrawer
        client={selected}
        isOpen={drawerOpen}
        close={closeDrawer}
        onRequestDelete={() => {
          setDeleteTarget(selected);
          closeDrawer();
        }}
      />

      <Modal
        isOpen={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        title={t('CLIENTS.DELETE_MODAL.TITLE', { defaultValue: 'Supprimer le client' })}
      >
        <DeleteClientModal
          client={deleteTarget}
          close={() => setDeleteTarget(null)}
          onDeleted={() => {
            fetchClients();
            setSelected(null);
          }}
        />
      </Modal>
    </>
  );
}

export default Clients;
